import React from "react"
import cx from "classnames"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationPrimaryActionProps = {
  /** Icon shown in both expanded and collapsed states. */
  icon: IconName
  /** Button label — visible when expanded, animated out when collapsed. */
  label: string
  /** Swap the rendered element — e.g. pass `<a href="..." />` or an Inertia `<Link href="..." />`. */
  render?: React.ReactElement
}

// =============================================================================
// Styles
// =============================================================================

const primaryActionStyles = [
  "inline-flex items-center justify-center align-middle text-center select-none",
  "w-full h-10 py-2 px-4 rounded mb-2",
  "font-sans font-semibold text-sm leading-6",
  "border border-solid border-transparent",
  "bg-gray-900 text-white hover:bg-gray-800",
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900",
  "transition-colors cursor-pointer"
]

const labelStyles =
  "overflow-hidden whitespace-nowrap transition-[max-width,margin-left] duration-200"

// =============================================================================
// Component
// =============================================================================

/**
 * A full-width primary call-to-action button for the sidebar navigation.
 * Renders the label alongside the icon when expanded, and animates the label
 * out (keeping the icon centered) when the sidebar collapses.
 *
 * Pass a `render` prop to inject a link component for routing.
 *
 * @example
 * <Navigation.PrimaryAction icon="pencil-write" label="Start writing" render={<a href="/campaigns/new/steps" />} />
 */
export function NavigationPrimaryAction({
  icon,
  label,
  render
}: NavigationPrimaryActionProps) {
  const { collapsed } = useNavigationContext()

  return useRender({
    defaultTagName: "a",
    render,
    props: {
      "data-slot": "navigation-primary-action",
      className: cx(primaryActionStyles),
      children: (
        <>
          <Icon
            name={icon}
            className="shrink-0 pointer-events-none"
            aria-hidden
          />
          <span
            className={cx(labelStyles, collapsed ? "max-w-0" : "max-w-xs ml-2")}
          >
            {label}
          </span>
        </>
      )
    }
  })
}

NavigationPrimaryAction.displayName = "Navigation.PrimaryAction"
